import { AlertTriangle, Copy, ExternalLink, History, Link2 } from "lucide-react";
import { useEffect, useRef, type JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { toast } from "@/shared/lib/toast";
import { Badge } from "@/shared/ui/badge";
import { Drawer } from "@/shared/ui/drawer";
import { Skeleton } from "@/shared/ui/skeleton";

import { useAuditEvent } from "../api/use-audit-event";
import { moduleLabel, resultIntent, resultLabel } from "../lib/labels";
import {
  describeEvent,
  eventRelatedTo,
  fieldLabel,
  targetLabel,
  targetTypeLabel,
  valueLabel,
} from "../lib/summary";
import { targetToHref } from "../lib/target-link";

type GlobalAuditEvent = components["schemas"]["GlobalAuditEvent"];

/** `occurrences`/`first_seen`/`last_seen` only exist on a grouped LIST row —
 * GET /v1/audit-events/{id} returns a single event and cannot know its group.
 * The page therefore hands the selected row's group info down. */
export type AuditGroupInfo = Pick<GlobalAuditEvent, "occurrences" | "first_seen" | "last_seen">;

interface AuditDetailDrawerProps {
  /** Task 1's `AuditFilterState.event_id` — undefined means closed. */
  eventId: number | undefined;
  /** Group counts carried over from the list row that opened this drawer. */
  groupInfo?: AuditGroupInfo;
  onClose: () => void;
  /** Fired with the open event's exact `correlation_id` when "Related
   * events" is activated. The caller (AuditLogPage) owns closing the drawer
   * and rewriting the URL — this component only reports the correlation ID. */
  onRelatedEvents: (correlationId: string) => void;
  /** Fired with `order:<id>` / `sku:<id>` when a timeline action is activated. */
  onTimeline: (relatedTo: string) => void;
}

const SOURCE_SET = new Set(["backoffice", "csv", "sino_api", "scheduler", "system"]);

// Full-precision (seconds) local formatting — the table (AuditTable.tsx)
// only formats to the minute; the drawer's "exact timestamp" requirement
// needs strictly more precision than that summary view.
const exactTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});
const clockFormatter = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function formatExactLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return exactTimeFormatter.format(d);
}

function formatClock(iso: string | undefined): string {
  if (iso === undefined) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return clockFormatter.format(d);
}

const FIELD_ROW = "grid grid-cols-[124px,1fr] gap-3 py-1.5 text-sm";
const FIELD_LABEL = "text-[var(--text-subtle)]";
const SECTION_TITLE =
  "mb-2 text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]";
const ACTION_BTN =
  "inline-flex h-8 items-center gap-1.5 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-2.5 text-xs font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring";

export function AuditDetailDrawer({
  eventId,
  groupInfo,
  onClose,
  onRelatedEvents,
  onTimeline,
}: AuditDetailDrawerProps): JSX.Element {
  const { t } = useTranslation("audit");
  const open = eventId !== undefined;
  const { data: event, isLoading, isError, refetch } = useAuditEvent(eventId);
  const targetHref = event ? targetToHref(event) : null;
  const described = event ? describeEvent(t, event) : null;
  const relatedTo = event ? eventRelatedTo(event) : {};

  // Focus restoration (accessibility): capture whatever had focus right
  // before the drawer opens — with AuditTable's tabIndex=0 rows, that's the
  // triggering row itself, for both mouse click and keyboard Enter — and
  // give it focus back once the drawer closes, so closing never drops
  // keyboard focus into the void.
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      restoreFocusRef.current = document.activeElement as HTMLElement | null;
    } else if (restoreFocusRef.current) {
      restoreFocusRef.current.focus();
      restoreFocusRef.current = null;
    }
  }, [open]);

  function copyCorrelationId(e: GlobalAuditEvent): void {
    void navigator.clipboard?.writeText?.(e.correlation_id);
    toast.info(t("drawer.copy_correlation_success"));
  }

  const changesEntries = Object.entries(event?.changes ?? {});
  const hasChanges = changesEntries.length > 0;
  const hasAuthSection = event?.module === "authentication" && Boolean(event.actor_email);
  const occurrences = groupInfo?.occurrences ?? 1;
  const needsAttention = described !== null && described.fixSteps !== null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      title={<span data-testid="audit-detail-title">{described?.title ?? t("drawer.title")}</span>}
      subtitle={
        event
          ? t("drawer.subtitle", {
              module: moduleLabel(t, event.module),
              time: formatExactLocal(event.occurred_at),
            })
          : undefined
      }
    >
      <div className="px-5 py-4">
        {isLoading && (
          <div className="space-y-3" data-testid="audit-detail-loading">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-5 w-full" />
            ))}
          </div>
        )}

        {isError && !isLoading && (
          <div
            className="flex flex-col items-center gap-3 py-10 text-center"
            data-testid="audit-detail-error"
          >
            <p className="text-sm text-[var(--text-subtle)]">{t("drawer.error_body")}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg border border-[var(--premium-border)] px-4 py-1.5 text-sm font-medium transition hover:bg-[var(--canvas)] focus-ring"
            >
              {t("drawer.retry")}
            </button>
          </div>
        )}

        {event && described && !isLoading && !isError && (
          <div className="space-y-5" data-testid="audit-detail-content">
            {/* Outcome first: badge, then the "this still needs a human" cue. */}
            <div className="flex items-center gap-2">
              <Badge
                intent={resultIntent(event.result)}
                dot
                data-testid="audit-detail-result-badge"
              >
                {resultLabel(t, event.result)}
              </Badge>
              {needsAttention && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--intent-danger-fg)]"
                  data-testid="audit-detail-needs-attention"
                >
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                  {t("drawer.needs_attention")}
                </span>
              )}
            </div>

            <section data-testid="audit-detail-summary">
              <div className={SECTION_TITLE}>{t("drawer.what_happened")}</div>
              {described.summary.split("\n\n").map((paragraph) => (
                <p key={paragraph} className="mb-1.5 text-sm text-[var(--text-primary)]">
                  {paragraph}
                </p>
              ))}
            </section>

            {described.fixSteps && (
              <section data-testid="audit-detail-fix">
                <div className={SECTION_TITLE}>{t("drawer.to_fix")}</div>
                <ol className="list-decimal space-y-1 pl-5 text-sm text-[var(--text-primary)]">
                  {described.fixSteps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </section>
            )}

            {(described.links.order !== undefined ||
              described.links.sku !== undefined ||
              relatedTo.order !== undefined ||
              relatedTo.sku !== undefined) && (
              <div className="flex flex-wrap gap-2" data-testid="audit-detail-actions">
                {described.links.order !== undefined && (
                  <a
                    href={described.links.order}
                    onClick={onClose}
                    data-testid="audit-detail-open-order"
                    className={ACTION_BTN}
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    {t("drawer.open_order")}
                  </a>
                )}
                {described.links.sku !== undefined && (
                  <a
                    href={described.links.sku}
                    onClick={onClose}
                    data-testid="audit-detail-open-sku"
                    className={ACTION_BTN}
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    {t("drawer.open_sku")}
                  </a>
                )}
                {relatedTo.order !== undefined && (
                  <button
                    type="button"
                    onClick={() => onTimeline(`order:${relatedTo.order}`)}
                    data-testid="audit-detail-timeline-order"
                    className={ACTION_BTN}
                  >
                    <History className="h-3.5 w-3.5" aria-hidden />
                    {t("drawer.timeline_order")}
                  </button>
                )}
                {relatedTo.sku !== undefined && (
                  <button
                    type="button"
                    onClick={() => onTimeline(`sku:${relatedTo.sku}`)}
                    data-testid="audit-detail-timeline-sku"
                    className={ACTION_BTN}
                  >
                    <History className="h-3.5 w-3.5" aria-hidden />
                    {t("drawer.timeline_sku")}
                  </button>
                )}
              </div>
            )}

            <section data-testid="audit-detail-details">
              <div className={SECTION_TITLE}>{t("drawer.details_title")}</div>

              <div className={FIELD_ROW}>
                <span className={FIELD_LABEL}>{t("drawer.field_time")}</span>
                <div>
                  <time dateTime={event.occurred_at}>{formatExactLocal(event.occurred_at)}</time>
                  <div className="text-xs text-[var(--text-subtle)]" data-testid="audit-detail-utc">
                    {t("drawer.field_time_utc", { utc: event.occurred_at })}
                  </div>
                </div>
              </div>

              {occurrences > 1 && (
                <div className={FIELD_ROW} data-testid="audit-detail-occurrences">
                  <span className={FIELD_LABEL}>{t("drawer.field_occurrences")}</span>
                  <span>
                    {t("drawer.occurrences", {
                      count: occurrences,
                      first: formatClock(groupInfo?.first_seen),
                      last: formatClock(groupInfo?.last_seen ?? event.occurred_at),
                    })}
                  </span>
                </div>
              )}

              <div className={FIELD_ROW}>
                <span className={FIELD_LABEL}>{t("drawer.field_actor")}</span>
                <div>
                  <div className="font-semibold text-[var(--text-primary)]">
                    {event.actor_display_name}
                  </div>
                  {event.actor_email && (
                    <div className="text-xs text-[var(--text-subtle)]">{event.actor_email}</div>
                  )}
                </div>
              </div>

              <div className={FIELD_ROW}>
                <span className={FIELD_LABEL}>{t("drawer.field_source")}</span>
                <span>
                  {SOURCE_SET.has(event.source) ? t(`source.${event.source}`) : event.source}
                </span>
              </div>

              <div className={FIELD_ROW}>
                <span className={FIELD_LABEL}>{t("drawer.field_target")}</span>
                <div>
                  {targetHref ? (
                    <a
                      href={targetHref}
                      onClick={onClose}
                      data-testid="audit-detail-target-link"
                      className="font-semibold text-[var(--brand-600)] underline-offset-2 hover:underline focus-ring"
                    >
                      {targetLabel(event)}
                    </a>
                  ) : (
                    <div className="font-semibold text-[var(--text-primary)]">
                      {targetLabel(event)}
                    </div>
                  )}
                  <div className="text-xs text-[var(--text-subtle)]">
                    {targetTypeLabel(t, event.target_type)}
                  </div>
                </div>
              </div>

              {described.details.map((detail) => (
                <div key={detail.label} className={FIELD_ROW}>
                  <span className={FIELD_LABEL}>{detail.label}</span>
                  {detail.href !== undefined ? (
                    <a
                      href={detail.href}
                      onClick={onClose}
                      className="break-words font-semibold text-[var(--brand-600)] underline-offset-2 hover:underline focus-ring"
                    >
                      {detail.value}
                    </a>
                  ) : (
                    <span className="break-words">{detail.value}</span>
                  )}
                </div>
              ))}
            </section>

            {hasChanges && (
              <section data-testid="audit-detail-changes">
                <div className={SECTION_TITLE}>{t("drawer.changes_title")}</div>
                <div className="overflow-hidden rounded-lg border border-[var(--premium-border)]">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-[var(--premium-border)] bg-[var(--canvas)]">
                        <th className="px-3 py-2 text-left font-semibold text-[var(--text-subtle)]">
                          {t("drawer.changes_field")}
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-[var(--text-subtle)]">
                          {t("drawer.changes_before")}
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-[var(--text-subtle)]">
                          {t("drawer.changes_after")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--row-divider)]">
                      {changesEntries.map(([field, change]) => (
                        <tr key={field}>
                          <td className="px-3 py-2 font-semibold text-[var(--text-primary)]">
                            {fieldLabel(t, field)}
                          </td>
                          <td className="px-3 py-2 text-[var(--text-subtle)]">
                            <pre className="whitespace-pre-wrap break-all font-mono">
                              {valueLabel(t, field, change.old)}
                            </pre>
                          </td>
                          <td className="px-3 py-2 text-[var(--text-primary)]">
                            <pre className="whitespace-pre-wrap break-all font-mono">
                              {valueLabel(t, field, change.new)}
                            </pre>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {hasAuthSection && (
              <section data-testid="audit-detail-auth">
                <div className={SECTION_TITLE}>{t("drawer.auth_title")}</div>
                <div className="space-y-1.5 rounded-lg border border-[var(--premium-border)] bg-[var(--canvas)] px-3 py-2.5 text-sm">
                  <div className={FIELD_ROW}>
                    <span className={FIELD_LABEL}>{t("drawer.auth_attempted_email")}</span>
                    <span>{event.actor_email}</span>
                  </div>
                  {event.ip_address && (
                    <div className={FIELD_ROW}>
                      <span className={FIELD_LABEL}>{t("drawer.auth_ip_address")}</span>
                      <span className="font-mono">{event.ip_address}</span>
                    </div>
                  )}
                  {event.user_agent && (
                    <div className={FIELD_ROW}>
                      <span className={FIELD_LABEL}>{t("drawer.auth_user_agent")}</span>
                      <span className="break-all">{event.user_agent}</span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Engineering escape hatch, deliberately last and collapsed: a
                correlation ID binds one HTTP request, not one business story,
                so it answers far fewer operator questions than the sections
                above. */}
            <details
              className="rounded-lg border border-[var(--premium-border)] bg-[var(--canvas)] px-3 py-2"
              data-testid="audit-detail-correlation"
            >
              <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] focus-ring">
                {t("drawer.correlation_summary")}
              </summary>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <div
                  className="min-w-0 truncate font-mono text-xs text-[var(--text-secondary)]"
                  data-testid="audit-detail-correlation-id"
                >
                  {event.correlation_id}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => copyCorrelationId(event)}
                    data-testid="audit-detail-copy-correlation"
                    className={ACTION_BTN}
                  >
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                    {t("drawer.copy_correlation")}
                  </button>
                  <button
                    type="button"
                    onClick={() => onRelatedEvents(event.correlation_id)}
                    data-testid="audit-detail-related-events"
                    className={ACTION_BTN}
                  >
                    <Link2 className="h-3.5 w-3.5" aria-hidden />
                    {t("drawer.related_events")}
                  </button>
                </div>
              </div>
            </details>
          </div>
        )}
      </div>
    </Drawer>
  );
}
